import React, { FC, ReactElement, ReactNode } from 'react';

import { Icon } from '@iconify/react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import FallbackLetterAvatar from '~/components/atoms/FallbackLetterAvatar';
import { getPersonalityConf } from '~/modules/chat/components/Chat/elements/Personality';
import { PERSONALITY } from '~/modules/chat/types';
import { useAppSelector } from '~/store/hooks/useAppSelector';

import styles from './styles.module.scss';

export enum AvatarType {
  SYSTEM = 'system',
  PROMO = 'promo',
  PERSONALITY = 'personality',
  USER = 'user',
  AI_EDIT = 'ai_edit',
}

type Props =
  | {
      children: ReactNode;
      avatarType: Exclude<AvatarType, AvatarType.PERSONALITY>;
      personality?: PERSONALITY;
      className?: string;
    }
  | {
      children: ReactNode;
      avatarType: AvatarType.PERSONALITY;
      personality: PERSONALITY;
      className?: string;
    };

const LogoMessageWrapper: FC<Props> = ({
  children,
  avatarType,
  personality,
  className,
}) => {
  const user = useAppSelector((state) => state.profile);
  const { t } = useTranslation();

  const personalityConf = getPersonalityConf(t);

  const renderLogo = (): ReactElement => {
    switch (avatarType) {
      case AvatarType.SYSTEM: {
        return (
          <FallbackLetterAvatar variant={avatarType}>
            <Icon icon="material-symbols:auto-awesome-outline" />
          </FallbackLetterAvatar>
        );
      }
      case AvatarType.PROMO: {
        return (
          <FallbackLetterAvatar variant={avatarType}>
            <Icon icon="material-symbols:auto-awesome-outline" />
          </FallbackLetterAvatar>
        );
      }
      case AvatarType.PERSONALITY:
        return (
          <FallbackLetterAvatar variant={avatarType}>
            {personalityConf[personality]?.logo}
          </FallbackLetterAvatar>
        );
      case AvatarType.USER:
        return (
          <FallbackLetterAvatar logo_src={user.avatar} variant={avatarType}>
            <span>{user.name.charAt(0)}</span>
          </FallbackLetterAvatar>
        );
      case AvatarType.AI_EDIT: {
        return (
          <FallbackLetterAvatar variant={avatarType}>
            <Icon icon="material-symbols:auto-awesome-outline" />
          </FallbackLetterAvatar>
        );
      }
    }
  };

  return (
    <div className={clsx(styles.message, className, styles[avatarType])}>
      <div className={styles.message__inner_flex}>
        {renderLogo()}
        <div className={styles.message__content}>{children}</div>
      </div>
    </div>
  );
};

export default LogoMessageWrapper;
